import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { useGetAiModelsQuery } from "@/pages/Private/redux/media/media.api";

import { usePageTitle } from "@/utils/usePageTitle";

import { AiModelsTable } from "./components/AiModelsTable";
import { FormatPresetsTable } from "./components/FormatPresetsTable";
import { MusicLibraryTable } from "./components/MusicLibraryTable";
import { UnverifiedPricingNotice } from "./components/UnverifiedPricingNotice";

/**
 * Platform admin surface for the three global tables behind AI media: output formats, the
 * model registry, and the background-music library. Adding a model, changing a price or
 * putting a track in front of every company is an edit here, never a code change.
 *
 * The music library sits here rather than on a route of its own because it is the same
 * kind of thing as the other two — one global list every company draws from, editable only
 * by a platform admin — and it is reached from the same navigation entry. The company cost
 * overview does get its own route: it is a different audience and a different permission.
 */
export const MediaPresetsPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`mediaPresets.${key}`);

	usePageTitle(ts("title"));

	const { data: models } = useGetAiModelsQuery();
	const unverifiedCount = (models ?? []).filter((model) => !model.pricesVerified).length;

	return (
		<div className="space-y-5 p-6 pb-10">
			<p className="max-w-page text-sm text-subtle">{ts("description")}</p>

			<UnverifiedPricingNotice unverifiedCount={unverifiedCount} />

			<FormatPresetsTable />
			<AiModelsTable />
			<MusicLibraryTable />
		</div>
	);
};
